import { ACOES, PAPEIS, type CodigoDePapel, type Permissao } from '@locaveri/shared'
import { LoaderCircle, RotateCcw, ShieldAlert, ShieldCheck } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCatalogoDePermissoes,
  useDefinirPermissoesDoPapel,
  usePapeis,
  type PapelDaApi,
} from '@/features/rbac/api'
import {
  MOTIVO_SEM_ESCRITA,
  MOTIVO_SUPER_ADMIN,
  agruparPorRecurso,
  contarAlteracoes,
  estadoDaCelula,
  type ContextoDaCelula,
  type LinhaDeRecurso,
} from '@/features/rbac/matriz'
import {
  RECURSOS_DE_ADMINISTRACAO,
  ROTULO_DA_ACAO,
  ROTULO_DO_RECURSO,
} from '@/features/rbac/rotulos'
import { errorMessage } from '@/lib/api'
import { usePermissao, usePermissoes } from '@/lib/permissoes'

/**
 * PERFIS E PERMISSÕES — o que cada perfil acessa, decidido pelo gestor.
 *
 * ===========================================================================
 * O PODER JÁ EXISTIA; FALTAVA O BOTÃO
 * ===========================================================================
 * O CLAUDE.md manda "permissões por recurso+ação em tabela, não hardcoded em
 * `if`", e é assim desde a Fase 1: `role_permissions` é a verdade e
 * `PERMISSOES_POR_PAPEL` é só o padrão do seed. A API expõe a edição desde
 * então. Nenhuma tela a dirigia — o gestor tinha a capacidade e não tinha onde
 * clicar, o que na prática é o mesmo que não ter.
 *
 * ===========================================================================
 * OS DOIS 403 APARECEM ANTES DO CLIQUE
 * ===========================================================================
 * A API recusa conceder o que o solicitante não tem, e recusa editar o
 * SUPER_ADMIN. Descobrir isso no `Salvar`, depois de marcar trinta caixas, é
 * perder a edição inteira num erro que não diz qual caixa causou. Então:
 *
 *   - a caixa que você não pode conceder vem DESABILITADA, com o motivo no
 *     título — e some do contador de alterações;
 *   - o SUPER_ADMIN abre em modo leitura, com a explicação de por que ele é
 *     intocável: é o único papel capaz de reabrir o controle de acesso, e
 *     editá-lo permite trancar a instalação do lado de fora.
 *
 * ===========================================================================
 * A MATRIZ É A TELA, E A ORDEM NÃO É ALFABÉTICA
 * ===========================================================================
 * Recurso na linha, ação na coluna: é a forma do dado (`recurso.acao`) e a
 * única que deixa "o financeiro vê pagamento mas não edita" ser lido de uma
 * olhada. Os recursos de ADMINISTRAÇÃO vêm num bloco à parte, no fim: em ordem
 * alfabética, "Perfis de acesso" cairia entre "Pagamentos" e "Pontos de
 * retirada", e a caixa mais perigosa da tela ficaria indistinguível das outras.
 *
 * Célula VAZIA e caixa DESMARCADA são coisas diferentes, e a legenda diz isso:
 * vazia é "esta combinação não existe no catálogo" (não há
 * `dashboard.approve`), desmarcada é "existe e este perfil não tem".
 */

export function PermissoesPage(): ReactElement {
  const podeEditar = usePermissao('role.update')
  const minhasPermissoes = usePermissoes()

  const papeis = usePapeis()
  const catalogo = useCatalogoDePermissoes()
  const salvar = useDefinirPermissoesDoPapel()

  const [papelEscolhido, setPapelEscolhido] = useState<CodigoDePapel | null>(null)
  /** `null` = ainda não mexeram; o que vale é a concessão que veio da API. */
  const [rascunho, setRascunho] = useState<ReadonlySet<string> | null>(null)

  const listaDePapeis = papeis.data?.itens ?? []
  const linhas = useMemo(
    () => agruparPorRecurso(catalogo.data?.itens ?? []),
    [catalogo.data?.itens],
  )

  // O primeiro papel da lista quando nada foi escolhido: uma tela que abre sem
  // seleção pede um clique para mostrar qualquer coisa.
  const papel: PapelDaApi | undefined =
    listaDePapeis.find((item) => item.codigo === papelEscolhido) ?? listaDePapeis[0]

  const concedidasNaApi = useMemo(
    () => new Set<string>(papel?.permissoes ?? []),
    [papel?.permissoes],
  )
  const marcadas = rascunho ?? concedidasNaApi

  const ehSuperAdmin = papel?.codigo === 'SUPER_ADMIN'
  const somenteLeitura = !podeEditar || ehSuperAdmin

  const contextoDaCelula: ContextoDaCelula = {
    marcadas,
    minhas: minhasPermissoes,
    somenteLeitura,
    motivoDaLeitura: ehSuperAdmin ? MOTIVO_SUPER_ADMIN : MOTIVO_SEM_ESCRITA,
  }

  const alteracoes = useMemo(
    () => (rascunho === null ? 0 : contarAlteracoes(rascunho, concedidasNaApi)),
    [rascunho, concedidasNaApi],
  )

  function alternar(chave: string, marcar: boolean): void {
    const proximo = new Set(marcadas)

    if (marcar) {
      proximo.add(chave)
    } else {
      proximo.delete(chave)
    }

    setRascunho(proximo)
  }

  function trocarDePapel(codigo: CodigoDePapel): void {
    // Trocar de papel DESCARTA o rascunho, e a confirmação existe porque o
    // contrário — carregar o rascunho de um papel sobre outro — seria copiar
    // permissões entre perfis sem ninguém pedir.
    if (alteracoes > 0 && !window.confirm('Há alterações não salvas neste perfil. Descartar?')) {
      return
    }

    setRascunho(null)
    setPapelEscolhido(codigo)
  }

  function confirmar(): void {
    if (papel === undefined || rascunho === null) {
      return
    }

    salvar.mutate(
      { codigo: papel.codigo, permissoes: [...rascunho] as readonly Permissao[] },
      {
        onSuccess: () => {
          setRascunho(null)
          toast.success(`Permissões de ${papel.nome} atualizadas.`)
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  if (papeis.isPending || catalogo.isPending) {
    return <LoadingState rotulo="Carregando perfis…" linhas={4} />
  }

  if (papeis.isError || catalogo.isError) {
    return (
      <ErrorState
        titulo="Não foi possível carregar os perfis"
        erro={papeis.error ?? catalogo.error}
        aoTentarNovamente={() => {
          void papeis.refetch()
          void catalogo.refetch()
        }}
      />
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Perfis e permissões</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          O que cada perfil acessa, por recurso e ação. A concessão vive em tabela — mudar aqui vale
          na hora, sem publicação de versão.
        </p>
      </div>

      {/* ------------------------------------------------------------------
          Escolha do perfil
          ------------------------------------------------------------------ */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Perfis de acesso">
        {listaDePapeis.map((item) => {
          const ativo = item.codigo === papel?.codigo

          return (
            <button
              key={item.codigo}
              type="button"
              role="tab"
              aria-selected={ativo}
              onClick={() => {
                trocarDePapel(item.codigo)
              }}
              className={[
                'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                ativo
                  ? 'border-brand-500 bg-brand-tint text-foreground'
                  : 'border-border bg-surface text-fg-secondary hover:bg-elevated',
              ].join(' ')}
            >
              <span className="block font-medium">{item.nome}</span>
              <span className="block text-xs tabular-money text-fg-muted">
                {item.usuarios} {item.usuarios === 1 ? 'usuário' : 'usuários'} ·{' '}
                {item.permissoes.length} permissões
              </span>
            </button>
          )
        })}
      </div>

      {papel === undefined ? null : (
        <>
          <div className="rounded-lg border border-border bg-surface p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-foreground">{papel.nome}</h2>
                <p className="mt-1 max-w-prose text-sm text-fg-secondary">
                  {PAPEIS[papel.codigo].descricao}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {papel.exige2fa ? (
                  <Badge variant="info">
                    <ShieldCheck aria-hidden="true" />
                    Exige 2FA
                  </Badge>
                ) : null}
                {papel.atribuivel ? null : <Badge variant="neutral">Não atribuível</Badge>}
                {papel.ehInterno ? <Badge variant="neutral">Equipe interna</Badge> : null}
              </div>
            </div>
          </div>

          {ehSuperAdmin ? (
            <Alert variant="warning">
              <AlertTitle>O super administrador não é editável</AlertTitle>
              <AlertDescription>
                É o único perfil capaz de reabrir o controle de acesso. Se ele pudesse perder
                permissões, daria para trancar a instalação inteira do lado de fora — sem caminho de
                volta por nenhuma tela. A API recusa a edição; esta lista é só leitura.
              </AlertDescription>
            </Alert>
          ) : null}

          {!podeEditar && !ehSuperAdmin ? (
            <Alert variant="neutral">
              <AlertTitle>Você está vendo, não editando</AlertTitle>
              <AlertDescription>
                Editar perfis exige a permissão <code>role.update</code>.
              </AlertDescription>
            </Alert>
          ) : null}

          {papel.codigo === 'VISITANTE' ? (
            <Alert variant="info">
              <AlertTitle>Este é o perfil do site público</AlertTitle>
              <AlertDescription>
                Ele não pertence a nenhuma pessoa: é resolvido pelo token da sessão anônima de quem
                navega no site sem conta. É aqui que se desliga a cotação pública sem publicar
                versão.
              </AlertDescription>
            </Alert>
          ) : null}

          <MatrizDePermissoes
            linhas={linhas.filter((linha) => !RECURSOS_DE_ADMINISTRACAO.has(linha.recurso))}
            titulo="Operação"
            descricao="Frota, preço, reserva, financeiro e o site público."
            contexto={contextoDaCelula}
            aoAlternar={alternar}
          />

          <MatrizDePermissoes
            linhas={linhas.filter((linha) => RECURSOS_DE_ADMINISTRACAO.has(linha.recurso))}
            titulo="Administração do sistema"
            descricao="Quem mexe aqui muda quem pode mexer em tudo. Conceda com parcimônia."
            contexto={contextoDaCelula}
            aoAlternar={alternar}
          />

          {somenteLeitura ? null : (
            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface/95 p-4 backdrop-blur">
              <p className="text-sm text-fg-secondary">
                {alteracoes === 0
                  ? 'Nenhuma alteração.'
                  : `${String(alteracoes)} ${alteracoes === 1 ? 'alteração' : 'alterações'} não salvas.`}
              </p>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={alteracoes === 0 || salvar.isPending}
                  onClick={() => {
                    setRascunho(null)
                  }}
                >
                  <RotateCcw aria-hidden="true" />
                  Descartar
                </Button>
                <Button disabled={alteracoes === 0 || salvar.isPending} onClick={confirmar}>
                  {salvar.isPending ? <LoaderCircle className="animate-spin" /> : null}
                  Salvar permissões
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface MatrizProps {
  readonly linhas: readonly LinhaDeRecurso[]
  readonly titulo: string
  readonly descricao: string
  readonly contexto: ContextoDaCelula
  readonly aoAlternar: (chave: string, marcar: boolean) => void
}

function MatrizDePermissoes({
  linhas,
  titulo,
  descricao,
  contexto,
  aoAlternar,
}: MatrizProps): ReactElement | null {
  if (linhas.length === 0) {
    return null
  }

  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-base font-semibold tracking-tight">{titulo}</h2>
        <p className="text-sm text-fg-secondary">{descricao}</p>
      </div>

      {/* A matriz é larga por natureza: oito colunas de ação mais o recurso.
          O contêiner rola no eixo X para a página nunca rolar — a regra de
          responsividade em 375px. */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableCaption className="sr-only">
            Permissões de {titulo.toLowerCase()}, por recurso e ação
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-56">Recurso</TableHead>
              {ACOES.map((acao) => (
                <TableHead key={acao} className="text-center">
                  {ROTULO_DA_ACAO[acao]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {linhas.map((linha) => (
              <TableRow key={linha.recurso}>
                <TableCell>
                  <span className="block text-sm text-foreground">
                    {ROTULO_DO_RECURSO[linha.recurso]}
                  </span>
                  <code className="block text-xs text-fg-muted">{linha.recurso}</code>
                </TableCell>

                {ACOES.map((acao) => {
                  const celula = estadoDaCelula(linha, acao, contexto)

                  if (celula.tipo === 'inexistente') {
                    // Traço, não caixa: esta combinação não existe no catálogo.
                    // É diferente de "existe e não foi concedida" — ver a legenda.
                    return (
                      <TableCell key={acao} className="text-center">
                        <span aria-hidden="true" className="text-fg-muted">
                          —
                        </span>
                        <span className="sr-only">não existe</span>
                      </TableCell>
                    )
                  }

                  const permissao = linha.porAcao.get(acao)

                  return (
                    <TableCell key={acao} className="text-center">
                      <Checkbox
                        checked={celula.marcada}
                        disabled={celula.tipo === 'travada'}
                        aria-label={`${ROTULO_DA_ACAO[acao]} · ${ROTULO_DO_RECURSO[linha.recurso]}`}
                        title={celula.tipo === 'travada' ? celula.motivo : celula.descricao}
                        onChange={(evento) => {
                          if (permissao !== undefined) {
                            aoAlternar(permissao.chave, evento.target.checked)
                          }
                        }}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-fg-muted">
        <ShieldAlert aria-hidden="true" className="mr-1 inline size-3" />
        Traço (—) é combinação que não existe no catálogo; caixa desmarcada é permissão que existe e
        este perfil não tem. Caixa cinza é permissão que você mesmo não possui — ninguém concede o
        que não tem.
      </p>
    </section>
  )
}
